import {
  ShowcaseCreateReviewRequest,
  ShowcaseUpdateReviewRequest,
  type ShowcaseMyReviewDto,
} from '@repo/api/web/models'

import { useReviews } from './useReviews'
import { useReviewsConfig } from './useReviewsConfig'
import ReviewForm from '../components/ReviewForm.vue'
import { reviewSubmittedMessage } from '../utils/myReviews'
import {
  reviewApiErrorAction,
  reviewApiErrorMessage,
  reviewApiFieldErrors,
  type ReviewApiErrorLike,
} from '../utils/reviewApiErrors'
import {
  emptyReviewForm,
  fromMyReview,
  planReviewEdit,
  toCreateRequest,
  type ReviewFormConfig,
  type ReviewFormModel,
} from '../utils/reviewFormModel'

import { parseApiError } from '#Common/models/Error'

type Target
  = | { kind: 'create', productId: number, onDone?: () => void }
    | { kind: 'edit', reviewId: number, initial: ReviewFormModel, onDone?: () => void }

/** Пока настройки не загрузились, модалка не открывается; заглушка нужна только для типа пропа */
const NO_CONFIG: ReviewFormConfig = { allowMedia: false, maxPhotos: 0, allowRatingOnly: false, minTextLength: null }

/**
 * Модалка формы отзыва: создание (со страницы товара, из «Ожидают оценки», из заказа)
 * и правка своего отзыва. Анонима и неавторизованного покупателя вместо формы отправляет
 * на вход — как избранное.
 */
export function useReviewFormModal() {
  const { service, routing } = useReviews()
  const config = useReviewsConfig()
  const auth = useAuth()
  const authInteractive = useAuthInteractive()
  const toast = useToast()
  const { send, isLoading } = useRequest()

  const target = shallowRef<Target>()
  /** Ошибка последней отправки; ошибки API показываются в форме, а не тостом */
  const failure = shallowRef<ReviewApiErrorLike>()

  const uploadPhoto = (file: File) => service.uploadPhoto(file)

  /** Ссылка рядом с ошибкой: на уже оставленный отзыв — в «Мои отзывы», где его можно изменить */
  const apiErrorLink = () =>
    reviewApiErrorAction(failure.value) === 'my-reviews'
      ? { label: 'Перейти в «Мои отзывы»', to: routing.index.route }
      : undefined

  const { startAdd, startEdit } = useFormModal<ReviewFormModel>({
    formComponent: ReviewForm,
    overlayType: 'modal',
    formAttrs: {
      config: () => config.value ?? NO_CONFIG,
      loading: isLoading,
      apiError: () => reviewApiErrorMessage(failure.value),
      apiErrorLink,
      apiFieldErrors: () => reviewApiFieldErrors(failure.value),
      // Функцию передаём геттером: formAttrs разворачиваются через toValue
      upload: () => uploadPhoto,
    },
    factory: payload => payload,
    dictionaries: () => ({ title: target.value?.kind === 'edit' ? 'Изменить отзыв' : 'Отзыв о товаре' }),
    onSubmit: (model: ReviewFormModel) => save(model),
  })

  /** Отправка; результат — сохранённый отзыв (его статус решает текст благодарности) или undefined при правке одного имени */
  function submit(current: Target, model: ReviewFormModel) {
    return send(
      async (): Promise<ShowcaseMyReviewDto | undefined> => {
        if (current.kind === 'create')
          return service.create(current.productId, ShowcaseCreateReviewRequest.createLazy(toCreateRequest(model)))

        const plan = planReviewEdit(current.initial, model)
        // Сначала имя: без повторной модерации; если дальше упадёт содержимое, повтор флаг уже не отправит
        if (plan.hideAuthorName !== undefined) {
          await service.setHideAuthorName(current.reviewId, plan.hideAuthorName)
          current.initial.hideAuthorName = plan.hideAuthorName
        }
        if (plan.content)
          return service.update(current.reviewId, ShowcaseUpdateReviewRequest.createLazy(plan.content))
        return undefined
      },
      { throwError: true, silent: true },
    )
  }

  async function save(model: ReviewFormModel): Promise<boolean> {
    const current = target.value
    if (!current) return false
    failure.value = undefined

    let saved: ShowcaseMyReviewDto | undefined
    try {
      saved = await submit(current, model)
    } catch (error) {
      failure.value = parseApiError(error) ?? {}
      return false
    }

    // При премодерации отзыв ещё не виден на странице товара — говорим об этом прямо (AC-3.6)
    toast.add({ color: 'success', description: reviewSubmittedMessage(current.kind, saved?.status) })
    current.onDone?.()
    return true
  }

  function openCreate(productId: number, opts: { orderItemId?: number, onDone?: () => void } = {}) {
    if (!config.value?.enabled) return
    if (!auth.support.checkAuth() || auth.support.checkIsAnonymous()) {
      authInteractive.openAuth()
      return
    }
    target.value = { kind: 'create', productId, onDone: opts.onDone }
    failure.value = undefined
    startAdd(emptyReviewForm(opts.orderItemId))
  }

  function openEdit(review: ShowcaseMyReviewDto, onDone?: () => void) {
    if (!config.value?.enabled) return
    // Исходная модель — своя копия: форма правит другую, а после смены имени её флаг обновляется
    target.value = { kind: 'edit', reviewId: review.id, initial: fromMyReview(review), onDone }
    failure.value = undefined
    startEdit(fromMyReview(review))
  }

  return { openCreate, openEdit }
}
